// src/web/client/src/pages/index.jsx - Fixed Complete Routing
// ======= GLOBAL PROTECTIONS - MUST BE FIRST =======
if (typeof window !== 'undefined' && !window.__PROTECTIONS_INITIALIZED) {
    // 1. Protection against undefined in API calls
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      if (url && typeof url === 'string' && url.includes('undefined')) {
        console.warn('🚫 Blocked API call with undefined:', url);
        return Promise.resolve({
          ok: false,
          status: 400,
          statusText: 'Bad Request - undefined parameter',
          json: () => Promise.resolve({ error: 'Invalid request parameters' }),
          text: () => Promise.resolve('Invalid request parameters')
        });
      }
      return originalFetch.apply(this, args);
    };
    
    // NOTE: the old "initialize shop_domain to demo-shop" block was removed
    // on purpose (data integrity): an unknown store must surface an honest
    // error state, never silently show demo data as the merchant's own.
    // Demo mode is entered ONLY explicitly (DemoModeButton / Login demo).

    window.__PROTECTIONS_INITIALIZED = true;
  }
  
  // ======= IMPORTS =======
  import React from 'react';
  import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
  
  // Pages
  import Landing from './Landing';
  import BillingSuccess from './BillingSuccess';
  import Login from './Login';
  import Onboarding from './Onboarding';
  import Home from './Home';
  import Customers from './Customers';
  import Automations from './Automations';
  import Activity from './Activity';
  import Results from './Results';
  import Settings from './Settings';
  import Billing from './Billing';
  import Help from './Help';
  import AuthSuccess from './AuthSuccess';
  import Layout from './Layout';
  import DevTools from './DevTools';
import AuthError from './AuthError';
import { PlatformGate, PLATFORM_PAGES } from './Platform';
import { LegacyRedirect, ProToolsRedirect, AnalyticsRedirect } from './legacyRoutes';
import PrivacyPolicy from './PrivacyPolicy';
import SubscriptionGuard from '@/components/subscription/SubscriptionGuard';

  // Components
  import PrivateRoute from '../components/PrivateRoute';
  import RootEntry from '../components/RootEntry';
  

// Shopify Param Handler Component
function ShopifyParamHandler() {
  const location = useLocation();
  
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shop = urlParams.get('shop');
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    // Handle OAuth callback
    if (code && state && shop) {
      console.log('🔐 OAuth callback detected in index.jsx');
      window.location.href = `https://api.ecomoria.com/auth/callback?${urlParams.toString()}`;
      return;
    }
    
    // Handle initial Shopify load
    if (shop && !code) {
      console.log('🛍️ Shopify initial load:', shop);
      localStorage.setItem('shop_domain', shop);
      localStorage.setItem('shopDomain', shop);
      
      // Check authentication
      const token = localStorage.getItem('token');
      if (token && token !== 'demo-token') {
        // Already authenticated
        if (window.location.pathname === '/' && window.top !== window.self) {
          // Embedded and on landing - go to dashboard
          window.location.href = '/dashboard';
        }
      }
    }
  }, [location]);
  
  return null;
}

  // ======= URL NORMALIZATION =======
  function normalizeUrl(pathname) {
    // Convert /storesettings to /store-settings
    const urlMapping = {
      '/storesettings': '/store-settings',
      '/StoreSettings': '/store-settings',
      '/storessettings': '/store-settings'
    };
    
    return urlMapping[pathname] || pathname;
  }
  
  // ======= ROUTE NORMALIZER COMPONENT =======
  function RouteNormalizer({ children }) {
    const location = useLocation();
    const normalizedPath = normalizeUrl(location.pathname);
    
    React.useEffect(() => {
      if (normalizedPath !== location.pathname) {
        window.history.replaceState(null, '', normalizedPath);
      }
    }, [location.pathname, normalizedPath]);
    
    return children;
  }
  
  // ======= PAGES WITH LAYOUT =======
  function PagesWithLayout() {
    const location = useLocation();
    
    return (
        <Routes>
        <Route path="/" element={<Layout />}>
          {/* The six pages (UX brief, Phase B). Old routes below redirect into
              them — with the query string kept, so an embedded entry's
              shop/host/id_token and a tab name both survive. */}
          <Route index element={<LegacyRedirect to="/home" />} />
          <Route path="home" element={<Home />} />
          <Route path="customers" element={<Customers />} />
          <Route path="customers/:id" element={<PlatformGate><PLATFORM_PAGES.customer /></PlatformGate>} />
          <Route path="automations" element={<Automations />} />
          <Route path="activity" element={<Activity />} />
          <Route path="results" element={<Results />} />
          <Route path="settings" element={<Settings />} />
          <Route path="billing" element={<Billing />} />
          <Route path="help" element={<Help />} />

          {/* Old routes → new homes */}
          <Route path="dashboard" element={<LegacyRedirect to="/home" />} />
          <Route path="brain" element={<LegacyRedirect to="/home" />} />
          <Route path="platform" element={<LegacyRedirect to="/home" />} />
          <Route path="orders" element={<LegacyRedirect to="/customers" tab="orders" />} />
          <Route path="messages" element={<LegacyRedirect to="/automations" tab="templates" />} />
          <Route path="replenish" element={<LegacyRedirect to="/automations" />} />
          <Route path="replenish/reminders" element={<LegacyRedirect to="/automations" />} />
          <Route path="replenish/setup" element={<LegacyRedirect to="/automations" />} />
          <Route path="replenish/settings" element={<LegacyRedirect to="/settings" tab="products" />} />
          <Route path="reviews" element={<LegacyRedirect to="/automations" />} />
          <Route path="loyalty" element={<LegacyRedirect to="/automations" />} />
          <Route path="pro-tools" element={<ProToolsRedirect />} />
          <Route path="timeline" element={<LegacyRedirect to="/activity" />} />
          {/* /analytics?tab=… → the matching Results section (?section=). */}
          <Route path="analytics" element={<AnalyticsRedirect />} />
          <Route path="revenue" element={<LegacyRedirect to="/results" tab="revenue" param="section" />} />
          <Route path="store-settings" element={<LegacyRedirect to="/settings" />} />
          <Route path="storesettings" element={<LegacyRedirect to="/settings" />} />
        </Route>
        {/* Routes without Layout */}
        <Route path="/login" element={<Login />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/AuthSuccess" element={<AuthSuccess />} />
          <Route path="/AuthError" element={<AuthError />} />
          <Route path="/BillingSuccess" element={<BillingSuccess />} />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    );
  }
  
  // ======= MAIN APP COMPONENT =======
  /*export default function Pages() {
    return (
      <Router>
              <ShopifyParamHandler /> 
        <RouteNormalizer>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/auth/success" element={<AuthSuccess />} /> 
            <Route path="/*" element={
              <PrivateRoute>
                <PagesWithLayout />
              </PrivateRoute>
            } />
          </Routes>
        </RouteNormalizer>
      </Router>
    );
  }*/

  export default function Pages() {
    return (
      <Router>
        <SubscriptionGuard>
          <ShopifyParamHandler />
          <RouteNormalizer>
            <Routes>
            {/* RootEntry: embedded-with-shop (installed by construction) →
                straight into the app with the query string preserved; everyone
                else gets Landing exactly as before. OAuth callbacks (?code)
                pass through untouched — ShopifyParamHandler owns that case. */}
            <Route path="/" element={<RootEntry />} />
            <Route path="/login" element={<Login />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/auth/success" element={<AuthSuccess />} />
            {/* Public privacy policy — required for the Shopify App Store
                listing. MUST be registered before the catch-all /* route so
                React Router matches it first and it never enters PrivateRoute. */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/*" element={
              <PrivateRoute>
                <PagesWithLayout />
              </PrivateRoute>
            } />
                        </Routes>
          </RouteNormalizer>
        </SubscriptionGuard>
      </Router>
    );
  }